import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    Logout,
    CheckAccessToken,
    GetAccessToken,
    Response,
    SetAccessToken,
    StorageGet,
    StorageSet
} from '@mdd/api';
//import { Angulartics2 } from 'angulartics2';
import { BehaviorSubject } from 'rxjs';
import { GetCurrentUser } from '../domain/interactors/account';
import { User } from '../domain/models';
import { Principal } from '../presentation/delegates';
import { Subscriptions } from '../util';
import { CacheService } from './cache.service';
import { EnvironmentService } from './environment.service';
import { createRouterLocation } from './guards/createRouterLocation';
import { MddHttpService } from './mdd-http.service';
import i18next from 'i18next';
import { UserSettings } from '@mdd/elements/card-configs/helpers';

const USER_ID_MAP_KEY = 'userIdMap';
interface UserIDMap {
    [userName: string]: number;
}

@Injectable()
export class PrincipalService implements Principal {
    user = new BehaviorSubject<User | undefined>(undefined);
    shouldStoreUserActivity = new BehaviorSubject<boolean>(false);
    private apiLogout: Logout;
    private getAccessToken = new GetAccessToken();
    private setAccessToken = new SetAccessToken();
    private checkAccessToken = new CheckAccessToken();
    private getUserIdMap = new StorageGet<UserIDMap>();
    private setUserIdMap = new StorageSet<UserIDMap>();
    private getCurrentUser: GetCurrentUser;
    private subs = new Subscriptions();

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        cache: CacheService,
        mddHttp: MddHttpService,
        //private ana: Angulartics2,
        private env: EnvironmentService
    ) {
        mddHttp.error.subscribe((response: Response<{}>) => {
            if (response.status === 401 && this.user.value) {
                this.logout();
            }
        });

        this.apiLogout = new Logout(mddHttp);

        this.getCurrentUser = new GetCurrentUser(mddHttp);

        this.getCurrentUser.execute().then((user) => {
            const userPreferences = user.userPreferences;
            const selectedLanguage =
                userPreferences && userPreferences.language && userPreferences.language.code;
            if (selectedLanguage && selectedLanguage !== i18next.language) {
                i18next.changeLanguage(selectedLanguage);
                document.location.reload();
            }
        });

        cache.update.subscribe((type) => {
            if (type === 'user') {
                this.updateUser();
            }
        });

        this.subs.add(
            this.user.subscribe((user) => {
                let userId = '';
                if (user && user.id) {
                    // Update a local email -> userId map for use below
                    let userIdMap = this.getUserIdMap.execute(USER_ID_MAP_KEY);
                    if (!userIdMap) {
                        userIdMap = {};
                    }

                    userIdMap[user.email] = user.id;
                    this.setUserIdMap.execute({ key: USER_ID_MAP_KEY, value: userIdMap });
                    userId = `${this.env.getPrefix()}${user.id}`;
                }
                UserSettings.createFromUserPreferences(user && user.userPreferences);
                //this.ana.setUsername.next(userId);
                (window as any).dataLayer.push({
                    userId,
                    role: user && user.authorities.join(',')
                });
            })
        );

        // Getting the user ID is async, so first check if we know what the ID is
        // based on the access token's email. This will let us set the analytic
        // user ID immediately for the first page view.
        const token = this.getAccessToken.execute();
        if (token) {
            const userIdMap = this.getUserIdMap.execute(USER_ID_MAP_KEY);
            const userId = userIdMap && userIdMap[token.jwt.user_name];
            if (userId) {
                //this.ana.setUsername.next(`${this.env.getPrefix()}${userId}`);
            }
        }

        this.updateUser();
    }

    logout(queryParams?: any, noRedirect = false): Promise<boolean> {
        this.setAccessToken.execute(undefined!);
        GetCurrentUser.clearCache();
        this.user.next(undefined!);
        if (noRedirect) {
            return Promise.resolve(true);
        } else {
            return this.apiLogout.execute();
        }
    }

    updateUser() {
        if (
            this.checkAccessToken.execute({
                ...createRouterLocation(this.activatedRoute.snapshot, this.router),
                // TODO: investigate initial route navigation DASH-1535
                hash: window.location.hash,
                search: window.location.search
            })
        ) {
            this.getCurrentUser
                .execute({ forceClear: true })
                .then((user) => {
                    this.user.next(user);
                })
                .catch(() => {
                    if (this.user.value) {
                        this.logout();
                    }
                });
        }
    }
}
